import { useCallback, useEffect, useRef, useState } from "react";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";

import { Classification } from "@/types/classification";
import { ClassificationCard } from "./ClassificationCard";
import { DraggableIndicator } from "@/components/DraggableIndicator";

type DraggableClassificationBarProps = {
    classifications: Classification[];
    onClick?: (classification: Classification) => void;
    onReorder: (classifications: Classification[]) => void;
    variant?: "buttons" | "inline" | "inline-with-names";
};

const DraggableClassification = ({
    classification,
    index,
    onClick,
    variant = "buttons",
    onReorder,
}: {
    classification: Classification;
    index: number;
    onClick?: (classification: Classification) => void;
    variant?: "buttons" | "inline" | "inline-with-names";
    onReorder?: (classifications: Classification[]) => void;
}) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<{
        x: number;
        y: number;
    } | null>(null);

    const startDrag = (clientX: number, clientY: number) => {
        if (!elementRef.current || !onReorder) return;

        const rect = elementRef.current.getBoundingClientRect();
        setDragOffset({
            x: clientX - rect.left,
            y: clientY - rect.top,
        });
        setIsDragging(true);
    };

    const updateDragPosition = useCallback(
        (clientX: number, clientY: number) => {
            if (!dragOffset || !elementRef.current || !isDragging) return;

            const newX = clientX - dragOffset.x;
            const newY = clientY - dragOffset.y;

            elementRef.current.style.position = "fixed";
            elementRef.current.style.left = `${newX}px`;
            elementRef.current.style.top = `${newY}px`;
            elementRef.current.style.zIndex = "1000";
            elementRef.current.style.pointerEvents = "none";
        },
        [dragOffset, elementRef, isDragging]
    );

    const endDrag = useCallback(() => {
        if (!elementRef.current || !isDragging) return;

        elementRef.current.style.position = "relative";
        elementRef.current.style.left = "";
        elementRef.current.style.top = "";
        elementRef.current.style.zIndex = "";
        elementRef.current.style.pointerEvents = "";

        setDragOffset(null);
        setIsDragging(false);
    }, [elementRef, isDragging]);

    // Touch event handlers

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!onReorder) return;
        const touch = e.touches[0];
        startDrag(touch.clientX, touch.clientY);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging) return;
        const touch = e.touches[0];
        updateDragPosition(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!isDragging) return;

        const touch = e.changedTouches[0];
        const deltaX = touch.clientX - (dragOffset?.x || 0);
        const deltaY = touch.clientY - (dragOffset?.y || 0);

        endDrag();

        // Handle reordering if there was significant movement
        if (Math.abs(deltaX) > 30 || Math.abs(deltaY) > 30) {
            const elementAtPoint = document.elementFromPoint(
                touch.clientX,
                touch.clientY
            );
            if (elementAtPoint && elementRef.current?.parentElement) {
                const targetClassification = elementAtPoint.closest(
                    ".draggable-classification"
                );
                if (
                    targetClassification &&
                    targetClassification !== elementRef.current
                ) {
                    const container = elementRef.current.parentElement;
                    const children = Array.from(container.children);
                    const targetIndex = Math.max(
                        children.findIndex(
                            (child) =>
                                child === targetClassification ||
                                child.contains(targetClassification)
                        ),
                        0
                    );

                    if (index !== targetIndex) {
                        const event = new CustomEvent("reorder", {
                            detail: { fromIndex: index, toIndex: targetIndex },
                        });
                        container.dispatchEvent(event);
                    }
                }
            }
        }
    };

    // Mouse event handlers

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!onReorder) return;
        startDrag(e.clientX, e.clientY);
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleGlobalMouseMove = (e: MouseEvent) => {
            updateDragPosition(e.clientX, e.clientY);
        };

        const handleGlobalMouseUp = (e: MouseEvent) => {
            if (!dragOffset) return;

            const deltaX = e.clientX - (dragOffset.x || 0);
            const deltaY = e.clientY - (dragOffset.y || 0);

            endDrag();

            // Handle reordering if there was significant movement
            if (Math.abs(deltaX) > 30 || Math.abs(deltaY) > 30) {
                const elementAtPoint = document.elementFromPoint(
                    e.clientX,
                    e.clientY
                );
                if (elementAtPoint && elementRef.current?.parentElement) {
                    const targetClassification = elementAtPoint.closest(
                        ".draggable-classification"
                    );
                    if (
                        targetClassification &&
                        targetClassification !== elementRef.current
                    ) {
                        const container = elementRef.current.parentElement;
                        const children = Array.from(container.children);
                        const targetIndex = Math.max(
                            children.findIndex(
                                (child) =>
                                    child === targetClassification ||
                                    child.contains(targetClassification)
                            ),
                            0
                        );

                        if (index !== targetIndex) {
                            const event = new CustomEvent("reorder", {
                                detail: {
                                    fromIndex: index,
                                    toIndex: targetIndex,
                                },
                            });
                            container.dispatchEvent(event);
                        }
                    }
                }
            }
        };

        document.addEventListener("mousemove", handleGlobalMouseMove);
        document.addEventListener("mouseup", handleGlobalMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleGlobalMouseMove);
            document.removeEventListener("mouseup", handleGlobalMouseUp);
        };
    }, [isDragging, dragOffset, index, endDrag, updateDragPosition]);

    return (
        <div
            ref={elementRef}
            style={{
                cursor: onReorder ? "grab" : "default",
                opacity: isDragging ? 0.8 : 1,
                transform: isDragging ? "rotate(5deg) scale(1.05)" : "none",
                transition: isDragging ? "none" : "all 0.2s ease",
                position: "relative",
                touchAction: "none",
                userSelect: "none",
                ...(isDragging ? { width: "60%" } : {}),
            }}
            className="draggable-classification"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <ClassificationCard
                classification={classification}
                onClick={onClick}
                variant={variant}
            />
            <DraggableIndicator />
        </div>
    );
};

export const DraggableClassificationBar = ({
    classifications,
    onClick,
    onReorder,
    variant = "buttons",
}: DraggableClassificationBarProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !onReorder) return;

        // Listen for custom reorder events from drag handling
        const handleReorderEvent = (e: CustomEvent) => {
            const { fromIndex, toIndex } = e.detail;
            if (fromIndex !== toIndex) {
                const newClassifications = reorder({
                    list: classifications,
                    startIndex: fromIndex,
                    finishIndex: toIndex,
                });
                onReorder(newClassifications);
            }
        };

        container.addEventListener(
            "reorder",
            handleReorderEvent as EventListener
        );

        return () => {
            if (container) {
                container.removeEventListener(
                    "reorder",
                    handleReorderEvent as EventListener
                );
            }
        };
    }, [classifications, onReorder]);

    return (
        <div
            ref={containerRef}
            className={`classification-bar-${variant} draggable-container`}
            style={{ touchAction: "none", userSelect: "none" }}
        >
            {classifications.map((classification, index) => (
                <DraggableClassification
                    key={classification.id}
                    classification={classification}
                    onClick={onClick}
                    variant={variant}
                    index={index}
                    onReorder={onReorder}
                />
            ))}
        </div>
    );
};
